import { useAtom } from 'jotai';
import type { AgentApiProfitsItem } from '@/types/serializers';
import { getProfitCardDateLine } from '../utils/getProfitCardDateLine';
import ProfitCardMemo from './ProfitCardMemo';
import ProfitStatusMenu, { type ProfitAction } from './ProfitStatusMenu';
import { currentSelectProfitAtom } from '../atoms/profit';
import clsx from 'clsx';

type Props = {
  profit: AgentApiProfitsItem;
  onAction: (profit: AgentApiProfitsItem, action: ProfitAction) => void;
  isSidebarOpen: boolean;
  openSidebar: (isValue: boolean) => void;
};

export default function ProfitCardItem({ profit, onAction, isSidebarOpen, openSidebar }: Props) {
  const [currentSelectProfit, setCurrentSelectProfit] = useAtom(currentSelectProfitAtom);
  const { primary, label } = getProfitCardDateLine(profit);

  return (
    <div
      className={clsx(
        'flex min-h-[80px] cursor-pointer items-center justify-around rounded bg-white px-6 shadow-[0px_0px_6px_0px_#00000033] hover:bg-gray',
        currentSelectProfit?.id === profit.id && isSidebarOpen && 'bg-gray!'
      )}
      onClick={() => {
        // 同じカードが既に選択中で、かつサイドバーが開いている場合のみトグルで閉じる
        if (isSidebarOpen && currentSelectProfit?.id === profit.id) {
          setCurrentSelectProfit(null);
          openSidebar(false);
          return;
        }
        setCurrentSelectProfit(profit);
        openSidebar(true);
      }}
    >
      {/* 候補者（名前+担当者、日付ラベル） */}
      <div className="flex w-[180px] min-w-[180px] flex-col justify-center gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <p className="min-w-0 truncate text-sm font-bold">
            {profit.candidate_profile?.name || '-'}
          </p>
          {profit.candidate_profile?.organization_users?.[0] && (
            <span className="inline-flex h-5 max-w-[7.5rem] shrink-0 items-center justify-center gap-0.5 truncate rounded-full border border-solid border-border bg-white px-2 text-[11px] leading-none whitespace-nowrap">
              {profit.candidate_profile.organization_users[0].name}
              {profit.candidate_profile.organization_users.length > 1 && (
                <span className="text-primary">
                  +{profit.candidate_profile.organization_users.length - 1}
                </span>
              )}
            </span>
          )}
        </div>
        {primary && (
          <p
            className={clsx(
              'min-w-0 truncate text-xs leading-[1.7]',
              profit.payment_overdue ? 'text-danger' : 'text-main'
            )}
          >
            {label != null && `${label} `}
            {primary}
          </p>
        )}
      </div>

      {/* 企業（名前 + ポジション名バッジ） */}
      <div className="flex w-[180px] min-w-[180px] flex-col justify-center gap-1.5">
        <div className="flex min-w-0 items-center gap-1">
          <p className="min-w-0 truncate text-sm">{profit.company?.name || '-'}</p>
          {profit.job_position_name && (
            <span className="inline-flex h-5 max-w-[7.5rem] shrink-0 items-center justify-center truncate rounded-full border border-solid border-border bg-white px-2 text-[11px] leading-none whitespace-nowrap">
              {profit.job_position_name}
            </span>
          )}
        </div>
      </div>

      {/* 売上金額 */}
      <div className="w-[130px] max-w-[130px] text-center">
        <p className="text-base font-bold">{profit.formatted_profit_value}</p>
      </div>

      {/* ステータス */}
      <div className="w-[180px] max-w-[180px]" onClick={(e) => e.stopPropagation()}>
        <ProfitStatusMenu profit={profit} onAction={onAction} />
      </div>

      {/* メモ */}
      <div
        className="w-[188px] max-w-[188px] min-w-[188px] overflow-hidden px-1"
        onClick={(e) => e.stopPropagation()}
      >
        <ProfitCardMemo profitId={profit.id} memo={profit.memo ?? ''} />
      </div>
    </div>
  );
}
